import {
  SyncCoreDataDefinitionsEnumTranslator,
  SyndicationErrorType,
  SyndicationStatus,
  SyndicationType,
} from '@edgebox/sync-core-data-definitions';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons/faSpinnerThird';
import { faLongArrowLeft } from '@fortawesome/pro-light-svg-icons/faLongArrowLeft';
import { faLongArrowRight } from '@fortawesome/pro-light-svg-icons/faLongArrowRight';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faHistory } from '@fortawesome/pro-light-svg-icons/faHistory';
import { faTimesCircle } from '@fortawesome/pro-light-svg-icons/faTimesCircle';
import { faCircleMinus } from '@fortawesome/pro-light-svg-icons/faCircleMinus';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { IconWithBackground } from './IconWithBackground';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faCircleDashed } from '@fortawesome/pro-light-svg-icons/faCircleDashed';
import { faCheck as faCheckSolid } from '@fortawesome/free-solid-svg-icons/faCheck';

export const neutralStatus = [SyndicationStatus.Aborted, SyndicationStatus.Skipped];
export const dangerStatus = [SyndicationStatus.Failed, SyndicationStatus.LimitExceeded];
export const doneStatus = [...dangerStatus, ...neutralStatus, SyndicationStatus.Finished];
export const warningStatus = [SyndicationStatus.Retrying];
export const issueStatus = [SyndicationStatus.Retrying, SyndicationStatus.Failed];

interface IProps {
  size?: '2x' | 'sm';
  status: SyndicationStatus;
  errorType?: SyndicationErrorType;
  background?: 'primary' | 'none';
  title?: string;
  type?: SyndicationType;
  foregroundClassName?: string;
  noMargin?: boolean;
  isLarge?: boolean;
}

function groupSyndicationStatus(status: SyndicationStatus, type?: SyndicationType) {
  const done = doneStatus.includes(status);
  const success = status === SyndicationStatus.Finished;
  const aborted = status === SyndicationStatus.Aborted;
  const skipped = status === SyndicationStatus.Skipped;
  const deleted =
    type &&
    [
      SyndicationType.DeleteEntity,
      SyndicationType.DeleteEmbeddedEntity,
      SyndicationType.RetrieveAndDeleteEmbeddedEntity,
      SyndicationType.RetrieveAndDeleteEntity,
    ].includes(type);

  return {
    done,
    success,
    aborted,
    skipped,
    deleted,
  };
}

export function getSyndicationStatusBackgroundIcon(status: SyndicationStatus, type?: SyndicationType) {
  const { skipped } = groupSyndicationStatus(status, type);

  if (skipped) {
    return faCircleDashed;
  }

  return null;
}

export function getSyndicationStatusIcon(status: SyndicationStatus, type?: SyndicationType) {
  const { done, success, aborted, skipped, deleted } = groupSyndicationStatus(status, type);
  if (done) {
    return aborted ? faCircleMinus : success ? (deleted ? faTrashCan : faCheckCircle) : skipped ? faCheck : faTimesCircle;
  }
  if (status === SyndicationStatus.Initializing) {
    return faSpinnerThird;
  }
  if (status === SyndicationStatus.Running) {
    return faArrowsRotate;
  }
  return faHistory;
}

export function getSyndicationStatusColor(status: SyndicationStatus, type?: SyndicationType, swapContrast = false, background = false) {
  const { done, success, aborted, skipped, deleted } = groupSyndicationStatus(status, type);
  if (done) {
    return aborted || skipped ? (background || swapContrast ? 'light' : 'dark') : success ? (deleted ? 'danger' : 'success') : 'danger';
  }
  if (status === SyndicationStatus.Initializing) {
    return 'light';
  }
  if (status === SyndicationStatus.Running) {
    return 'light';
  }
  return 'warning';
}

export function getSyndicationStatusTitle(status: SyndicationStatus) {}

export class SyndicationStatusIcon extends React.Component<IProps, {}> {
  render() {
    const { status, size, errorType, background, type, foregroundClassName, noMargin, isLarge } = this.props;

    const swapColors = background === 'primary';
    const foregroundColor = swapColors ? 'white' : 'var(--primary)';
    const backgroundColor = swapColors ? 'var(--primary)' : 'white';

    const done = doneStatus.includes(status);

    const statusName = SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('SyndicationStatus', status);
    let title = this.props.title ? `${statusName}: ${this.props.title}` : statusName;

    let icon: React.ReactNode;

    const stack = 'fa-stack-1x';

    if (done) {
      const iconBackground = getSyndicationStatusBackgroundIcon(status, type);
      const { success, deleted } = groupSyndicationStatus(status, type);

      icon =
        success && !deleted ? (
          <FontAwesomeIcon
            title={title}
            icon={faCheckSolid}
            className={`${stack} text-white ${foregroundClassName || ''}`}
            style={{ fontSize: size === '2x' ? '1em' : '0.65em' }}
          />
        ) : (
          <FontAwesomeIcon
            title={title}
            icon={getSyndicationStatusIcon(status, type)}
            className={`${stack} text-${getSyndicationStatusColor(status, type, swapColors)} ${foregroundClassName || ''}`}
            style={{ fontSize: iconBackground ? (size === '2x' ? '1em' : '0.6em') : size === '2x' ? '1.6em' : '1em' }}
          />
        );

      if (iconBackground) {
        icon = (
          <>
            <FontAwesomeIcon
              title={title}
              icon={iconBackground}
              className={`${stack} text-${getSyndicationStatusColor(status, type, swapColors, true)} ${foregroundClassName || ''}`}
              style={{ fontSize: size === '2x' ? '1.6em' : '1em' }}
            />
            {icon}
          </>
        );
      }
    } else {
      const mustPoll = errorType === SyndicationErrorType.SiteMustPoll;
      if (mustPoll) {
        title = 'Site must poll';
      }

      // A deliberate wait for an external system is healthy, so it gets the same neutral
      // treatment as the polling wait above rather than the warning colour.
      const awaitingExternalJob = errorType === SyndicationErrorType.AwaitingExternalJob;
      if (awaitingExternalJob) {
        title = SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaskErrorType', SyndicationErrorType.AwaitingExternalJob);
      }

      const neutralWait = mustPoll || awaitingExternalJob;

      icon =
        status === SyndicationStatus.Initializing ? (
          <FontAwesomeIcon
            title={title}
            icon={faSpinnerThird}
            spin
            className={`${stack} ${foregroundClassName || ''}`}
            style={{ fontSize: size === '2x' ? '1.4em' : '1em', '--fa-primary-color': foregroundColor } as any}
          />
        ) : status === SyndicationStatus.Running ? (
          <>
            <FontAwesomeIcon
              title={title}
              icon={faLongArrowRight}
              transform="up-4"
              className={`${stack} faa-horizontal animated faa-slow ${foregroundClassName || ''}`}
              style={{ fontSize: size === '2x' ? '1.4em' : '1em', '--fa-primary-color': foregroundColor } as any}
            />
            <FontAwesomeIcon
              title={title}
              icon={faLongArrowLeft}
              transform="down-4"
              className={`${stack} faa-horizontal faa-reverse animated faa-slow ${foregroundClassName || ''}`}
              style={{ fontSize: size === '2x' ? '1.4em' : '1em', '--fa-primary-color': foregroundColor } as any}
            />
          </>
        ) : (
          <FontAwesomeIcon
            title={title}
            icon={faHistory}
            flip={'horizontal'}
            className={`${stack} ${neutralWait ? 'text-dark' : 'text-warning'} ${foregroundClassName || ''}`}
            style={{ fontSize: size === '2x' ? '1.4em' : '1em' }}
          />
        );
    }

    return (
      <>
        <IconWithBackground
          isLarge={isLarge}
          size={noMargin ? 'no-margin' : size}
          backgroundColor={
            status === SyndicationStatus.Finished && !groupSyndicationStatus(status, type).deleted
              ? // FIXME: Provide as mapped color variable.
                '#10b981'
              : status === SyndicationStatus.Retrying
                ? 'white'
                : backgroundColor
          }
          biggerBackground={status === SyndicationStatus.Retrying}
          stackOnly={background === 'none'}
        >
          {icon}
        </IconWithBackground>
      </>
    );
  }
}
